<template>
  <div
    tabindex="0"
    :class="themeColor"
    class="hero-block"
  >
    <div class="hero-block__image-wrapper">
      <one-url-epi-picture
        v-if="oneUrlEpiPicture"
        ref="heroImage"
        :sizes="sizeSettings"
        :image="backgroundImage"
      />
      <epi-picture
        v-else
        ref="heroImage"
        :image="backgroundImage"
        :use-picture="usePicture"
        :auto-aspect-ratio="false"
        :sizes="sizeSettings"
        :manual-dpr="$root.isSmallViewport ? 1.25 : null"
        class="hero-block__image"
      />
      <epi-property
        id="BackgroundImage"
        class="background-edit-button"
        text="Ändra herobild"
      />
    </div>
    <div
      ref="heroContent"
      :class="themeColor"
      class="hero-block__content-wrapper"
    >
      <div
        v-if="prefix || isEditable"
        v-edit="'Prefix'"
        class="hero-block__prefix"
      >
        {{ prefix }}
      </div>

      <h1
        v-if="title || isEditable"
        ref="heroTitle"
        v-edit="'Title'"
        class="hero-block__heading"
      >
        {{ title }}
      </h1>

      <div
        v-if="content || isEditable"
        class="hero-block__content preamble--hero-block"
      >
        <p v-edit="'Content'">
          {{ content }}
        </p>
      </div>

      <template v-if="appLinks.length > 0">
        <div class="hero-block__app-icons-wrapper">
          <a
            v-for="appLink in appLinks"
            :key="appLink.href"
            :href="appLink.href"
            class="hero-block__app-icons"
          >
            <svgicon
              :name="appLink.icon"
              :original="true"
              :fill="false"
              :class="appLink.iconClass"
            /><span
              class="visually-hidden"
              v-text="appLink.label"
            />
          </a>
        </div>
      </template>

      <template v-else>
        <a
          v-if="(primaryButtonLabel && primaryUrl) || isEditable"
          v-edit="'PrimaryButtonLabel'"
          :href="primaryUrl"
          class="button--primary button--48 button--fullwidth"
          @click="trackClick(true)"
        >
          {{ primaryButtonLabel }}
        </a>
        <epi-property
          v-if="primaryButtonLabel"
          id="primaryUrl"
          text="Knapplänk"
        />
        <a
          v-if="(secondaryButtonLabel && secondaryUrl) || isEditable"
          v-edit="'SecondaryButtonLabel'"
          :href="secondaryUrl"
          :class="{'button--white' : transparentBtn}"
          class="button--secondary button--48 button--fullwidth"
          @click="trackClick()"
        >
          {{ secondaryButtonLabel }}
        </a>
        <epi-property
          v-if="secondaryButtonLabel"
          id="secondaryUrl"
          text="Knapplänk"
        />
      </template>
    </div>

    <epi-picture
      v-if="brandingImage"
      :image="brandingImage"
      :use-picture="false"
      :auto-aspect-ratio="true"
      class="hero-block__sub-branding-logo-image"
      :fit-dimensions="true"
      :sizes="brandingDimensions"
    />
  </div>
</template>

<script>
import oneUrlEpiPicture from '@/vue/components/common/picture/oneUrlEpiPicture.vue'
import ScrollObserver from '@/utils/scrollObserver'
import dataLayer from '@/analytics/dataLayer'
import '#/AppStore/GooglePlay'
import '#/AppStore/AppStore'

function parallaxBackground (scrollData) {
	const scrollSpeed = scrollData.currentScroll * 0.2
	this.style.transform = `translateY(${scrollSpeed}px)`
}

function parallaxContent (scrollData) {
  if (this.themeColor !== 'hero-block--theme-transparent') {
    if (this.$root.currentSizeLte('sm')) {
      this.$refs.heroContent.style.transform = `translateY(0px)`
      return
    }

    const scrollSpeed = scrollData.currentScroll * 0.085
    this.$refs.heroContent.style.transform = `translateY(${scrollSpeed}px)`
  }
}

export default {
  name: "HeroBlock",
  components: {
    oneUrlEpiPicture
  },
  props: {
    brandingImage: {
      type: Object,
      default: null
    },
    theme: {
      type: String,
      default: "Default"
    },
    prefix: {
      type: String,
      default: ""
    },
    title: {
      type: String,
      default: ""
    },
    content: {
      type: String,
      default: ""
    },
    primaryButtonLabel: {
      type: String,
      default: ""
    },
    secondaryButtonLabel: {
      type: String,
      default: ""
    },
    primaryUrl: {
      type: String,
      default: ""
    },
    secondaryUrl: {
      type: String,
      default: ""
    },
    backgroundImage: {
      type: Object,
      default: null
    },
    name: {
      type: String,
      default: ""
    },
    contentId: {
      type: String,
      default: ""
    },
    imageSizes: {
      type: Object,
      default: () => {}
    },
    usePicture: {
      type: Boolean,
      default: false
    },
    renderingSettings: {
      type: Object,
      default () {
        return {}
      }
    },
    oneUrlEpiPicture: {
      type: Boolean,
      default: false
    },
  },
  data () {
    return {
      appIcons: {
        googlePlay: 'AppStore/GooglePlay',
        appStore: 'AppStore/AppStore',
      },
      appLinks: [],
      dimensions: {
        small: {
          height: 553,
          width: 768
        },
        medium: {
          height: 737,
          width: 1024
        },
        large: {
          height: 720,
          width: 1232
        }
      },
      brandingDimensions: {
        small: {
          height: 120,
          width: 120
        },
        medium: {
          height: 120,
          width: 120
        },
        large: {
          height: 176,
          width: 176
        }
      }
    }
  },
  computed: {
    sizeSettings () {
      const all = {
        ...this.dimensions, ...this.imageSizes
      }
      return  all
    },
    themeColor () {
      const color = {
        Green: 'hero-block--theme-green',
        Orange: 'hero-block--theme-orange',
        Yellow: 'hero-block--theme-yellow',
        Pink: 'hero-block--theme-pink',
        Blue: 'hero-block--theme-blue',
        Transparent: 'hero-block--theme-transparent',
        Default: 'hero-block--theme-default'
      }
      return color[this.theme]
    },
    transparentBtn () {
      if ( this.themeColor === 'hero-block--theme-transparent' || this.themeColor === 'hero-block--theme-default' ) {
        return false
      }
      return true
    }
  },
  beforeDestroy () {
    ScrollObserver.removeAllCallbacks(this)
    ScrollObserver.removeAllCallbacks(this.$refs.heroImage.$el)
  },
  created () {
    this.checkForAppStoreLinks()
  },
  mounted () {
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      ScrollObserver.addCallback(this.$refs.heroImage.$el, parallaxBackground)
      ScrollObserver.addCallback(this, parallaxContent)
    }
  },
  methods: {
    trackClick (primary) {
      let linkUrl = (primary ? this.primaryUrl : this.secondaryUrl)
      dataLayer.add({
        event: primary ? 'heroSelect' : 'heroSecondarySelect',
        linkName: primary ? this.primaryButtonLabel : this.secondaryButtonLabel,
        linkUrl: !/^https?:\/\//i.test(linkUrl) ? window.location.origin + linkUrl : linkUrl,
      })
    },
    checkForAppStoreLinks () {
      let googlePlayLink = {
        icon: this.appIcons.googlePlay,
        iconClass: 'google-play',
        label: 'Ladda ned på Google Play'
      }

      let appStoreLink = {
        icon: this.appIcons.appStore,
        iconClass: 'app-store',
        label: 'Hämta på App Store'
      }

      const GOOGLE_PLAY = '//play.google.com'
      const APP_STORE = '//itunes.apple.com'

      if (this.primaryUrl.includes(GOOGLE_PLAY)) {
        googlePlayLink.href = this.primaryUrl
        this.appLinks.push(googlePlayLink)
      } else if (this.primaryUrl.includes(APP_STORE)) {
        appStoreLink.href = this.primaryUrl
        this.appLinks.push(appStoreLink)
      }

      if (this.secondaryUrl.includes(GOOGLE_PLAY)) {
        googlePlayLink.href = this.secondaryUrl
        this.appLinks.push(googlePlayLink)
      } else if (this.secondaryUrl.includes(APP_STORE)) {
        appStoreLink.href = this.secondaryUrl
        this.appLinks.push(appStoreLink)
      }
    }
  }
}
</script>

<style lang="scss">
.hero-block {
  margin-bottom: 2em;
  margin-top: 0;
  position: relative;

  @include fullbleed;

  @include media-breakpoint-up (md) {
    margin-bottom: 0;
    margin-top: 1.5rem;

    &.hero-block--theme-transparent {
      margin-top: 1.5rem;
    }
  }

  @include media-breakpoint-up (lg) {
    min-height: 26rem;
  }

  @include media-breakpoint-up (xl) {
    margin-top: 2rem;

    &.hero-block--theme-transparent {
      margin-top: 2rem;
    }
  }

  &__content {
    p {
      @include font(text,5, "light");

      margin: 0.5rem 0;
    }
  }

  &__content-wrapper {
    background-color: $color-white;
    border-radius: 0.25rem;
    padding: 1rem 1.5rem 2.5rem;
    margin-right: 1rem;
    margin-left: 1rem;
    margin-top: -2rem;
    position: relative;

    $baseColors: (
      green: $color-green-lightest,
      yellow: $color-yellow-lightest,
      pink: $color-pink-lightest,
      blue: $color-blue-lightest,
      orange: $color-orange-lightest
    );

    $baseFontColors: (
      green: $color-green-dark,
      yellow: $color-yellow-dark,
      pink: $color-pink-dark,
      blue: $color-blue-dark,
      orange: $color-orange-dark,
    );

    @each $color, $value in $baseColors {
      &.hero-block--theme-#{"" + $color} {
        background: $value;

        .hero-block__heading,
        .hero-block__prefix {
          color: map-get($baseFontColors, $color);
        }

        .hero-block__content {
          color: $color-grey-dark;
        }
      }
    }

    &.hero-block--theme-transparent {
      @include media-breakpoint-up(md) {
        top: auto;
        bottom: 0;
        padding: 2rem;
        background: transparent;
      }

      @include media-breakpoint-up(md) {
        .hero-block__prefix,
        .hero-block__heading,
        .hero-block__content {
          color: $color-white;
        }
      }
    }

    @include media-breakpoint-up(md) {
      margin: 0;
      position: absolute;
      left: 1.5rem;
      top: -1.5rem;
      width: 46%;
    }

    @include media-breakpoint-up(lg) {
      padding: 1.5rem 2rem 3rem;
      width: 488px;
    }

    @include media-breakpoint-up(xl) {
      left: 3rem;
      top: -2.5rem;
      width: 28.5rem;
    }

    .button--primary {
      margin-top: 1rem;
    }

    .button--secondary {
      margin-top: 0.5rem;
    }
  }

  &__prefix {
    @include font(hand,2, "normal");

    color: $color-red;
  }

  &__heading {
    @include font(rubrik, 5, "black");

    margin: 0.5rem 0;
  }

  &__app-icons-wrapper {
    margin-top: 1.5rem;
  }

  &__app-icons {
    &:first-of-type {
      margin-right: 0.5rem;
    }

    .app-store,
    .google-play {
      width: 6.6rem;
      height: 2.5rem;
    }

    .google-play {
      width: 7em;
    }

    @include media-breakpoint-up (sm) {
      .app-store {
        width: 7.5rem;
      }

      .google-play {
        width: 8.4375rem;
      }
    }

    @include media-breakpoint-up (lg) {
      .app-store,
      .google-play {
        width: 9rem;
        height: 3rem;
      }

      .google-play {
        width: 10.125rem;
      }
    }
  }

  &__image-wrapper {
    position: relative;
    overflow: hidden;
  }

  &__sub-branding-logo-image,
  &__sub-branding-logo-image.epi-picture {
    width: 7.5rem;
    position: absolute;
    top: 1rem;
    right: 1rem;

    @include media-breakpoint-up(sm) {
      top: 1.5rem;
      right: 1.5rem;
    }

    @include media-breakpoint-up(lg) {
      width: 11rem;
    }

    &.empty-image-placeholder {
      display: none;
    }
  }

  .background-edit-button {
    bottom: 1rem;
    position: absolute;
    right: 1rem;
    z-index: 1;
  }

  .epi-background-container,
  .empty-image-placeholder {
    padding-bottom: 75%;
    background-size: cover;

    @include media-breakpoint-up(md) {
      padding-bottom: 75%;
    }

    @include media-breakpoint-up(lg) {
      padding-bottom: 58.442%;
    }

    @include media-breakpoint-up(xl) {
      border-radius: 4px;
    }
  }
}

</style>
